import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { LeadView, MergedLeadRef } from "../types";

/**
 * Дубли одной компании.
 *
 * На выставке двое менеджеров легко ловят одного клиента, и к вечеру он в базе
 * дважды. Объединение делает эту карточку главной, а вторую убирает из списка:
 * её история и задачи переезжают сюда, пустые поля заполняются из неё.
 *
 * Ничего не удаляется — записи и расшифровки остаются на местах, объединение
 * можно отменить.
 */
export function LeadDuplicates({
  lead,
  onChanged,
}: {
  lead: LeadView;
  onChanged: (lead: LeadView) => void;
}) {
  const [duplicates, setDuplicates] = useState<LeadView[]>([]);
  const [merged, setMerged] = useState<MergedLeadRef[]>([]);
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .getLead(lead.meeting_id)
      .then((r) => {
        setDuplicates(r.duplicates);
        setMerged(r.merged);
      })
      .catch(() => {});
  }, [lead.meeting_id]);

  useEffect(load, [load]);

  const merge = async (donorId: number) => {
    setBusy(donorId);
    setError("");
    try {
      const r = await api.mergeLead(lead.meeting_id, donorId);
      setMerged(r.merged);
      onChanged(r.lead);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(0);
    }
  };

  const unmerge = async (donorId: number) => {
    setBusy(donorId);
    setError("");
    try {
      const r = await api.unmergeLead(lead.meeting_id, donorId);
      setMerged(r.merged);
      onChanged(r.lead);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(0);
    }
  };

  if (duplicates.length === 0 && merged.length === 0) return null;

  const title = (l: { company: string; contact_name: string }) =>
    [l.company, l.contact_name].filter(Boolean).join(" — ") || "без названия";

  return (
    <>
      <h3>Дубли</h3>

      {error && <div className="banner red">{error}</div>}

      {duplicates.length > 0 && (
        <>
          <p className="muted small" style={{ marginTop: 0 }}>
            Эта компания есть в базе ещё раз. Объединение оставит одну карточку:
            история и задачи перейдут сюда, пустые поля заполнятся. Ничего
            не удалится, действие можно отменить.
          </p>
          <ul className="list-clean">
            {duplicates.map((d) => (
              <li key={d.meeting_id} className="task">
                <span style={{ flex: 1 }}>
                  {title(d)}
                  <span className="muted small">
                    {d.owner_name ? ` · ${d.owner_name}` : ""}
                    {d.city ? ` · ${d.city}` : ""}
                  </span>
                </span>
                <Link className="btn sm" to={`/meetings/${d.meeting_id}`}>
                  Посмотреть
                </Link>
                <button
                  className="sm"
                  onClick={() => merge(d.meeting_id)}
                  disabled={busy === d.meeting_id}
                >
                  {busy === d.meeting_id ? "…" : "Присоединить"}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {merged.length > 0 && (
        <>
          <p className="muted small" style={{ marginBottom: 6 }}>
            Присоединено к этой карточке:
          </p>
          <ul className="list-clean">
            {merged.map((m) => (
              <li key={m.meeting_id} className="task">
                <span style={{ flex: 1 }}>{title(m)}</span>
                <Link className="btn sm" to={`/meetings/${m.meeting_id}`}>
                  Запись
                </Link>
                <button
                  className="sm ghost"
                  onClick={() => unmerge(m.meeting_id)}
                  disabled={busy === m.meeting_id}
                >
                  Отсоединить
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
